import { getUniqueID } from "../../utils/numbers";
import { DEFAULT_SUBTITLE_OPTIONS } from "../../components/Modal/ModalCreateWithIA/subtitlePresets";
import {
  VIDEO_EDITOR_MIN_CLIP_DURATION,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { getClipTimelineDuration, getTrackByType } from "./projectSchema";

const getSegmentText = (segment) => String(segment?.text || "").trim();

export const wordsForSegment = ({ segment, words = [] }) =>
  words.filter((word) => {
    const start = Number(word?.start);
    const end = Number(word?.end);
    return (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      end > Number(segment.start) &&
      start < Number(segment.end)
    );
  });

export const edlHasSubtitleClips = (edl) =>
  Object.values(edl?.clips?.byId || {}).some(
    (clip) => clip?.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE
  );

const ensureSubtitleTrack = (edl) => {
  const existing = getTrackByType({
    edl,
    type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
  });
  if (existing) {
    return { edl, track: existing };
  }

  const trackId = getUniqueID();
  const track = {
    id: trackId,
    type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
    clipIds: [],
  };
  const order = [...(edl.tracks?.order || [])];
  const videoIndex = order.findIndex(
    (id) => edl.tracks?.byId?.[id]?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
  );
  const insertAt = videoIndex >= 0 ? videoIndex : order.length;
  order.splice(insertAt, 0, trackId);

  return {
    track,
    edl: {
      ...edl,
      tracks: {
        byId: {
          ...(edl.tracks?.byId || {}),
          [trackId]: track,
        },
        order,
      },
    },
  };
};

export const applySubtitlesSidecarToEdl = ({ edl, subtitles }) => {
  if (!edl || !Array.isArray(subtitles?.segments) || subtitles.segments.length === 0) {
    return edl;
  }

  const ensured = ensureSubtitleTrack(edl);
  let nextEdl = ensured.edl;
  const trackId = ensured.track.id;
  const previousIds = new Set(ensured.track.clipIds || []);
  const clipsById = { ...nextEdl.clips.byId };
  previousIds.forEach((clipId) => {
    delete clipsById[clipId];
  });

  const clipIds = subtitles.segments.map((segment) => {
    const start = Math.max(0, Number(segment.start) || 0);
    const end = Math.max(
      start + VIDEO_EDITOR_MIN_CLIP_DURATION,
      Number(segment.end) || 0
    );
    const text = getSegmentText(segment);
    const clipId = getUniqueID();
    clipsById[clipId] = {
      id: clipId,
      trackId,
      type: VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
      sourceUrl: "",
      label: text,
      text,
      words: wordsForSegment({ segment, words: subtitles.words }),
      inPoint: 0,
      outPoint: end - start,
      position: start,
    };
    return clipId;
  });

  return {
    ...nextEdl,
    subtitleOptions: subtitles.options || DEFAULT_SUBTITLE_OPTIONS,
    subtitleWords: subtitles.words || [],
    tracks: {
      ...nextEdl.tracks,
      byId: {
        ...nextEdl.tracks.byId,
        [trackId]: {
          ...nextEdl.tracks.byId[trackId],
          clipIds,
        },
      },
    },
    clips: { byId: clipsById },
  };
};

const tokenizeSubtitleText = (text) =>
  String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

export const realignSubtitleWords = ({
  words = [],
  text,
  start,
  end,
}) => {
  const tokens = tokenizeSubtitleText(text);
  const clipStart = Number(start) || 0;
  const clipEnd = Math.max(clipStart, Number(end) || 0);
  const source = Array.isArray(words) ? words : [];

  if (tokens.length === 0) {
    return [];
  }

  if (source.length === tokens.length) {
    return source.map((word, index) => ({
      ...word,
      word: tokens[index],
    }));
  }

  return [
    {
      word: tokens.join(" "),
      start: clipStart,
      end: clipEnd,
    },
  ];
};

export const buildSubtitleClipTextUpdates = ({ clip, text }) => {
  const nextText = String(text || "").trim();
  const start = Number(clip?.position) || 0;
  const end = start + getClipTimelineDuration({ clip });
  return {
    text: nextText,
    label: nextText,
    words: realignSubtitleWords({
      words: clip?.words,
      text: nextText,
      start,
      end,
    }),
  };
};

export const areEditorSubtitlesEnabled = (options) =>
  options?.enabled !== false;

export const collectExportSubtitles = ({ edl }) => {
  if (!areEditorSubtitlesEnabled(edl?.subtitleOptions)) {
    return null;
  }

  const clips = Object.values(edl?.clips?.byId || {})
    .filter((clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE)
    .sort((left, right) => (left.position || 0) - (right.position || 0));

  if (clips.length === 0) {
    return null;
  }

  return {
    options: edl.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS,
    words: clips.flatMap((clip) => clip.words || []),
    segments: clips.map((clip) => {
      const start = Number(clip.position) || 0;
      return {
        start,
        end: start + getClipTimelineDuration({ clip }),
        text: clip.text || clip.label || "",
      };
    }),
  };
};

export const findActiveSubtitleClip = ({ clips = [], playhead = 0 }) => {
  const time = Number(playhead) || 0;
  return (
    clips.find((clip) => {
      if (clip.type !== VIDEO_EDITOR_TRACK_TYPE.SUBTITLE) {
        return false;
      }
      const start = Number(clip.position) || 0;
      const end = start + getClipTimelineDuration({ clip });
      return time >= start && time < end;
    }) || null
  );
};

export const clampSubtitleClipNeighbors = ({ edl, clipId, start, end }) => {
  const clip = edl?.clips?.byId?.[clipId];
  if (!clip || clip.type !== VIDEO_EDITOR_TRACK_TYPE.SUBTITLE) {
    return { start, end };
  }

  const siblings = (edl.tracks?.byId?.[clip.trackId]?.clipIds || [])
    .map((id) => edl.clips.byId[id])
    .filter((item) => item && item.id !== clipId)
    .sort((left, right) => (left.position || 0) - (right.position || 0));

  let nextStart = Math.max(0, Number(start) || 0);
  let nextEnd = Math.max(
    nextStart + VIDEO_EDITOR_MIN_CLIP_DURATION,
    Number(end) || 0
  );

  siblings.forEach((sibling) => {
    const siblingStart = Number(sibling.position) || 0;
    const siblingEnd = siblingStart + getClipTimelineDuration({ clip: sibling });
    if (siblingEnd <= (clip.position || 0) + 0.0001) {
      nextStart = Math.max(nextStart, siblingEnd);
    }
    if (siblingStart >= (clip.position || 0) + getClipTimelineDuration({ clip }) - 0.0001) {
      nextEnd = Math.min(nextEnd, siblingStart);
    }
  });

  if (nextEnd - nextStart < VIDEO_EDITOR_MIN_CLIP_DURATION) {
    nextEnd = nextStart + VIDEO_EDITOR_MIN_CLIP_DURATION;
  }

  return { start: nextStart, end: nextEnd };
};
